import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Mail,
  Shield,
  Lock,
  Eye,
  Globe,
  Database,
  UserCheck,
  Cookie,
  FileText,
  Home,
} from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const [openSections, setOpenSections] = useState(() =>
    policySections.map((section) => section.id),
  );

  const isOpen = (id) => openSections.includes(id);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id)
        ? prev.filter((sectionId) => sectionId !== id)
        : [...prev, id],
    );
  };

  const allOpen =
    policySections.length > 0 &&
    policySections.every((section) => openSections.includes(section.id));

  const collapseAll = () => {
    setOpenSections([]);
  };

  const expandAll = () => {
    setOpenSections(policySections.map((section) => section.id));
  };

  const toggleAllSections = () => {
    if (allOpen) {
      collapseAll();
    } else {
      expandAll();
    }
  };

  return (
    <div className="min-h-screen mt-16">
      {/* Page Header */}
      <section
        className="bg-[#0f2237] py-10 px-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
        }}
      >
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-white uppercase tracking-wide">
          Privacy Policy
        </h1>
        <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-primary" />
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <nav className="flex items-center gap-1.5 text-sm text-slate-500 min-w-0">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-navy transition-colors shrink-0"
            >
              <Home size={14} />
              <span>Home</span>
            </Link>

            <span className="text-slate-300 shrink-0">›</span>

            <span className="text-primary font-semibold truncate">
              Privacy Policy
            </span>
          </nav>

          <button
            type="button"
            onClick={toggleAllSections}
            className="
            inline-flex w-full sm:w-auto items-center justify-center gap-1.5
            rounded-lg border border-slate-200 bg-white px-4 py-2
            text-xs font-semibold text-slate-500 shadow-sm
            transition-colors hover:border-primary hover:text-navy
          "
          >
            <ChevronDown
              size={14}
              className="transition-transform duration-200 shrink-0"
              style={{
                transform: allOpen ? "rotate(0deg)" : "rotate(-90deg)",
              }}
            />

            <span>{allOpen ? "Collapse All" : "Expand All"}</span>
          </button>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-4">
          {policySections.map((section) => (
            <AccordionSection
              key={section.id}
              section={section}
              open={isOpen(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>

        {/* Contact */}
        <section className="mt-8 bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="border-t-2 border-primary p-5 sm:p-6 text-center">
            <h2 className="text-base font-bold text-[#0f2237] mb-2">
              Questions about this Privacy Policy?
            </h2>

            <p className="text-sm text-slate-600 mb-4">
              To ask questions about how your personal data is handled, please
              contact us.
            </p>

            <a
              href="mailto:support@rentgurus.com"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline transition"
            >
              <Mail className="h-4 w-4" />
              support@rentgurus.com
            </a>
          </div>
        </section>

        <footer className="mt-8 text-center text-xs text-slate-400">
          © 2026 RentGurus Inc. All rights reserved.
        </footer>
      </main>
    </div>
  );
};

const AccordionSection = ({ section, open, onToggle }) => {
  const Icon = section.icon;

  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left border-t-2 border-primary hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/80 text-white">
            <Icon className="h-4 w-4" />
          </span>

          <h2 className="text-sm sm:text-base font-bold text-[#0f2237]">
            {section.title}
          </h2>
        </div>

        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1">
          <div className="border-t border-slate-100 pt-4 text-sm text-slate-600 leading-6 space-y-4">
            {section.content}
          </div>
        </div>
      )}
    </section>
  );
};

const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, index) => (
      <li key={index} className="flex items-start gap-2">
        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const policySections = [
  {
    id: "introduction",
    title: "1. Introduction",
    icon: FileText,
    content: (
      <>
        <p>
          We respect your privacy and are committed to protecting your personal
          data. This Privacy Policy explains how we collect, use, store, and
          share your personal information when you interact with our website and
          services. It also explains your rights and how you can exercise them.
        </p>

        <p>
          For the purposes of this Privacy Policy, references to the “website”
          include:
        </p>

        <BulletList
          items={[
            "Our website and online booking platform",
            "Email communications",
            "Customer support interactions, including call centers",
          ]}
        />
      </>
    ),
  },
  {
    id: "personal-data",
    title: "2. What Is Personal Data",
    icon: Database,
    content: (
      <>
        <p>
          Personal data refers to any information that identifies or can
          reasonably be used to identify you, either directly or indirectly.
          This may include:
        </p>

        <BulletList
          items={[
            "Full name",
            "Postal address",
            "Email address",
            "Telephone number",
            "IP address",
            "Online identifiers and device information",
          ]}
        />

        <p>
          During the booking process, we typically collect personal data such as
          your name, contact details, and booking-related information.
        </p>

        <p>
          We do not intentionally collect sensitive personal data, such as
          health data, political opinions, or racial or ethnic origin, and we do
          not sell your personal data to third parties.
        </p>
      </>
    ),
  },
  {
    id: "collect",
    title: "3. How We Collect Personal Data",
    icon: UserCheck,
    content: (
      <>
        <p>We collect personal data in the following ways:</p>

        <BulletList
          items={[
            "When you make a booking or submit an enquiry through our website",
            "When you communicate with us via email or customer support",
            "When you subscribe to newsletters or marketing communications",
            "Through cookies, analytics tools, and similar tracking technologies",
          ]}
        />
      </>
    ),
  },
  {
    id: "categories",
    title: "4. Categories of Personal Data We Collect",
    icon: Eye,
    content: (
      <>
        <p>
          Depending on your interaction with us, we may collect and process the
          following categories of personal data:
        </p>

        <BulletList
          items={[
            "Identity Data: Name, email address, phone number",
            "Booking Data: Reservation details and rental-related information",
            "Technical Data: IP address, browser type, device details, and usage data",
            "Marketing Preferences: Communication and subscription preferences",
          ]}
        />

        <p>
          Payment card details are <strong>not stored or processed by us</strong>. All payment
          transactions are handled securely by third-party payment service
          providers.
        </p>
      </>
    ),
  },
  {
    id: "legal-basis",
    title: "5. Purpose and Legal Basis for Processing",
    icon: Shield,
    content: (
      <>
        <p>
          We process personal data only when there is a lawful basis to do so.
          These include:
        </p>

        <BulletList
          items={[
            "Contractual necessity: To process and manage your vehicle rental bookings and related services",
            "Legitimate interests: To improve our website, services, security, and customer experience",
            "Consent: To send marketing communications where you have opted in",
            "Legal obligations: To comply with applicable laws, regulations, fraud prevention, and security requirements",
          ]}
        />

        <p>
          The personal data processed for these purposes may include your name,
          email address, phone number, country or state of residence, and IP
          address.
        </p>
      </>
    ),
  },
  {
    id: "use",
    title: "6. How We Use Your Personal Data",
    icon: UserCheck,
    content: (
      <>
        <p>We use your personal data to:</p>

        <BulletList
          items={[
            "Process, confirm, and manage bookings",
            "Communicate with you regarding reservations and services",
            "Provide customer support and respond to enquiries",
            "Send service-related notifications and marketing communications, where permitted",
            "Improve website functionality, performance, and security",
            "Meet legal and regulatory obligations",
          ]}
        />
      </>
    ),
  },
  {
    id: "sharing",
    title: "7. Sharing of Personal Data",
    icon: Globe,
    content: (
      <>
        <p>
          We share personal data only when necessary and with trusted third
          parties, including:
        </p>

        <BulletList
          items={[
            "Vehicle rental providers: To facilitate and manage your booking",
            "Payment service providers: To securely process payments",
            "Technology, analytics, and marketing partners: To support website performance and promotions",
            "Regulatory or legal authorities: Where disclosure is required by law",
          ]}
        />

        <p>We do not sell your personal data.</p>
      </>
    ),
  },
  {
    id: "international",
    title: "8. International Data Transfers",
    icon: Globe,
    content: (
      <>
        <p>
          Some of our partners or service providers may be located outside
          Canada or the United States. Where personal data is transferred
          internationally, we take reasonable steps to ensure appropriate
          safeguards are in place, including contractual protections and
          security measures consistent with applicable data protection laws.
        </p>
      </>
    ),
  },
  {
    id: "security",
    title: "9. Data Security",
    icon: Lock,
    content: (
      <>
        <p>
          We implement appropriate technical and organizational measures to
          protect your personal data against unauthorized access, loss, misuse,
          or alteration. These measures include:
        </p>

        <BulletList
          items={[
            "Encryption of sensitive data",
            "Access controls and role-based permissions",
            "Secure servers, firewalls, and monitoring systems",
          ]}
        />
      </>
    ),
  },
  {
    id: "cookies",
    title: "10. Cookies and Tracking Technologies",
    icon: Cookie,
    content: (
      <>
        <p>
          We use cookies and similar technologies to operate our website,
          analyze usage, and enhance user experience. These technologies may
          collect technical information such as IP address, browser type, and
          browsing behavior.
        </p>

        <p>
          You may manage or disable cookies through your browser settings.
          Disabling cookies may affect certain website features.
        </p>
      </>
    ),
  },
  {
    id: "rights",
    title: "11. Your Privacy Rights",
    icon: Eye,
    content: (
      <>
        <p>
          Under applicable privacy and data protection laws in Canada and the
          United States, you may have the right to:
        </p>

        <BulletList
          items={[
            "Request access to your personal data",
            "Request correction of inaccurate or incomplete data",
            "Request deletion of your personal data, subject to legal requirements",
            "Object to or restrict certain types of processing",
            "Opt out of direct marketing communications at any time",
            "Withdraw consent where processing is based on consent",
            "Request information about how your personal data is shared",
            "Lodge a complaint with a relevant privacy or regulatory authority",
          ]}
        />

        <p>
          These rights may be subject to legal limitations depending on your
          jurisdiction.
        </p>
      </>
    ),
  },
  {
    id: "exercise-rights",
    title: "12. How to Exercise Your Rights",
    icon: Mail,
    content: (
      <>
        <p>
          To exercise your rights or if you have questions about this Privacy
          Policy, please contact us using the details below. We may need to
          verify your identity before processing your request.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "13. Contact Information",
    icon: Mail,
    content: (
      <>
        <p><strong>Email</strong>: support@rentgurus.com</p>
        <p><strong>Address</strong>: YourCompany Inc., [Registered Address – Canada / USA]</p>
      </>
    ),
  },
  {
    id: "updates",
    title: "14. Updates to This Privacy Policy",
    icon: FileText,
    content: (
      <>
        <p>
          We may update this Privacy Policy from time to time to reflect changes
          in legal requirements, technology, or our business practices. Updates
          will be posted on our website, and material changes may be
          communicated directly where required.
        </p>

        <p>
          <strong>Effective Date:</strong> [Insert Date]
        </p>
      </>
    ),
  },
];

export default PrivacyPolicy;
